import { useEffect, useRef, useState } from "react";
import { CrashRenderer } from "../renderer/CrashRenderer";
import { useGameStore } from "../store/gameStore";

/**
 * GAME CANVAS COMPONENT
 *
 * Renders the crash curve with GPU acceleration
 */

export function GameCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<CrashRenderer | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentRound = useGameStore((state) => state.currentRound);
  const [now, setNow] = useState(Date.now());

  // Ticker for timers
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(id);
  }, []);

  // Initialize renderer
  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    const renderer = new CrashRenderer({
      canvas: canvasRef.current,
      width,
      height,
    });

    renderer.start();
    rendererRef.current = renderer;

    // Handle resize
    const handleResize = () => {
      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;
      renderer.resize(newWidth, newHeight);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      renderer.destroy();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Update multiplier
  useEffect(() => {
    if (!rendererRef.current || !currentRound) return;

    if (currentRound.phase === "RUNNING" && currentRound.currentMultiplier) {
      const elapsed = currentRound.startTime
        ? Date.now() - currentRound.startTime
        : 0;

      rendererRef.current.updateMultiplier(
        currentRound.currentMultiplier,
        elapsed,
      );
    } else if (currentRound.phase === "CRASHED" && currentRound.crashPoint) {
      rendererRef.current.crash(currentRound.crashPoint);
    } else if (currentRound.phase === "BETTING") {
      rendererRef.current.reset();
    }
  }, [currentRound]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full bg-gradient-to-br from-slate-900 to-slate-800 rounded-lg overflow-hidden shadow-2xl"
    >
      <canvas ref={canvasRef} className="w-full h-full" />

      {/* Overlay info */}
      {currentRound && (
        <div className="absolute top-4 left-4 text-white">
          <div className="text-sm opacity-70">
            Round #{currentRound.roundNumber}
          </div>
          <div className="text-lg font-bold">
            {currentRound.phase === "BETTING" && "Place your bets!"}
            {currentRound.phase === "RUNNING" &&
              `${currentRound.currentMultiplier.toFixed(2)}x`}
            {currentRound.phase === "CRASHED" &&
              `Crashed at ${currentRound.crashPoint?.toFixed(2)}x`}
          </div>
          <div className="text-sm opacity-80 mt-1">
            {currentRound.phase === "BETTING" &&
              (() => {
                const startedAt = currentRound.bettingStartTime ?? now;
                const duration = currentRound.bettingDuration ?? 15000;
                const remaining = Math.max(0, duration - (now - startedAt));
                return `Betting ends in ${(remaining / 1000).toFixed(1)}s`;
              })()}
            {currentRound.phase === "RUNNING" &&
              (() => {
                const elapsed = currentRound.startTime
                  ? Math.max(0, now - currentRound.startTime)
                  : 0;
                return `Running for ${(elapsed / 1000).toFixed(1)}s`;
              })()}
            {currentRound.phase === "CRASHED" &&
              currentRound.crashTime &&
              `Crashed after ${(
                (currentRound.crashTime -
                  (currentRound.startTime ?? currentRound.crashTime)) /
                1000
              ).toFixed(1)}s`}
          </div>
        </div>
      )}
    </div>
  );
}
